import { useMutation } from "@apollo/client";
import { Stack, TextField, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import EncampAutocomplete from "components/Forms/EncampAutocomplete";
import { useState } from "react";

import {
  CREATE_WASTE_MANIFEST_VENDOR_ALIAS,
  VENDOR_DATA_COMPLETENESS_QUERY,
  WASTE_MANIFEST_VENDORS_TABLE_QUERY,
} from "./api";

type VendorOption = { id: string; name: string };

export interface MapVendorDialogProps {
  open: boolean;
  onClose: () => void;
  /** The raw, as-observed vendor spelling this dialog is aliasing. */
  observedName: string;
  /** Called once the alias is created successfully. */
  onMapped: () => void;
}

/**
 * Ties a raw, as-observed vendor spelling to an existing canonical
 * `WasteManifestVendor` by creating an alias — the "the data already exists,
 * it's just spelled differently than the canonical vendor" half of the scan
 * drawer's two mapping actions (the other being "Create vendor").
 */
export function MapVendorDialog({ open, onClose, observedName, onMapped }: MapVendorDialogProps) {
  const [selectedVendor, setSelectedVendor] = useState<VendorOption | null>(null);
  const [vendorSearch, setVendorSearch] = useState("");
  // Surfaced verbatim rather than sniffed/replaced — the server names the
  // vendor that already owns the alias, which is exactly what staff need to
  // resolve the clash.
  const [serverError, setServerError] = useState<string | null>(null);

  // Aliasing is what makes an observed spelling resolve to a canonical vendor,
  // so the grid behind this dialog changes the moment it succeeds: the row that
  // read as an unmapped spelling now rolls up under the real vendor. Refetch it
  // for the same reason every other mutation in this feature does — otherwise
  // staff close the drawer onto a grid that still shows the old, unmapped row.
  const [createAlias, { loading: saving }] = useMutation(CREATE_WASTE_MANIFEST_VENDOR_ALIAS, {
    refetchQueries: [VENDOR_DATA_COMPLETENESS_QUERY],
  });

  const resetAndClose = () => {
    setSelectedVendor(null);
    setVendorSearch("");
    setServerError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedVendor) return;
    setServerError(null);
    try {
      await createAlias({
        variables: { input: { wasteManifestVendorId: selectedVendor.id, alias: observedName } },
      });
      resetAndClose();
      onMapped();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="sm"
      fullWidth
      title="Map to vendor"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        disabled: !selectedVendor || saving,
        loading: saving,
        saveText: "Map",
      }}
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField label="Observed name" value={observedName} disabled fullWidth />
        <EncampAutocomplete
          query={WASTE_MANIFEST_VENDORS_TABLE_QUERY}
          getItems={(data) => data?.wasteManifestVendorsTable.items ?? []}
          getOptionLabel={(option) => option.name}
          variables={{
            search: vendorSearch,
            pageSize: 25,
            sort: [{ field: "name", sort: "asc" as const }],
          }}
          value={selectedVendor}
          onChange={(value) => {
            setSelectedVendor(value);
            setServerError(null);
          }}
          onInputChange={setVendorSearch}
          label="Vendor"
        />
        {serverError && (
          <Typography variant="body2" color="error">
            {serverError}
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
}
